import { Switch } from "@headlessui/react";

import { cn } from "../libs/cn";

const switchCheckedStyles = {
  primary: {
    checked: `bg-red-500`,
    unchecked: `bg-gray-400`,
  },
  secondary: {
    checked: `bg-green-500`,
    unchecked: `bg-gray-400`,
  },
};

const internalCheckedStyles = {
  checked: `translate-x-8`,
  unchecked: `translate-x-1`,
};

const textStyles = {
  common: `text-xs absolute font-medium`,
  on: `opacity-100`,
  off: `opacity-0`,
  checked: `left-2 text-white`,
  unchecked: `right-2 text-gray-400`,
};

const transitionStyles = `transition ease-in-out duration-200`;

const disabledStyles = "opacity-50";

export type ToggleProps = {
  label?: string;
  labelStyle?: string;
  checkedText?: string;
  uncheckedText?: string;
  checked?: boolean;
  disabled?: boolean;
  // will show the component as disabled, but it won't actually be disabled
  showAsDisabled?: boolean;
  onChange: (enabled: boolean) => void;
  className?: string;
  variant?: "primary" | "secondary";
  "data-test"?: string;
};

export function Toggle({
  label,
  labelStyle,
  checked = false,
  disabled,
  showAsDisabled,
  onChange,
  checkedText,
  uncheckedText,
  variant = "primary",
  className,
  "data-test": dataTest,
}: ToggleProps) {
  return (
    <Switch.Group>
      <div
        className={cn(
          "flex items-center",
          disabled || showAsDisabled ? disabledStyles : undefined,
          className
        )}
      >
        {label ? (
          <Switch.Label
            className={cn(
              "mr-3 block text-sm font-medium text-gray-700",
              labelStyle
            )}
          >
            {label}
          </Switch.Label>
        ) : null}
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          data-test={dataTest}
          className={cn(
            checked
              ? switchCheckedStyles[variant].checked
              : switchCheckedStyles[variant].unchecked,
            `relative inline-flex items-center h-7 rounded-full w-14`,
            !checked ? `opacity-50` : ``,
            transitionStyles
          )}
        >
          <span
            className={cn(
              checked
                ? internalCheckedStyles.checked
                : internalCheckedStyles.unchecked,
              `inline-block w-5 h-5 bg-white rounded-full transform`,
              transitionStyles
            )}
          />
          {checkedText && checked ? (
            <span
              className={cn(
                textStyles.common,
                textStyles.checked,
                transitionStyles
              )}
            >
              {checkedText}
            </span>
          ) : null}
          {uncheckedText && !checked ? (
            <span
              className={cn(
                textStyles.common,
                textStyles.unchecked,
                transitionStyles
              )}
            >
              {uncheckedText}
            </span>
          ) : null}
        </Switch>
      </div>
    </Switch.Group>
  );
}
